import { useRef, useState } from 'react';
import { Banner, Breadcrumb, Button, Icon, Segmented, SelectField, TextField } from './components/ui';
import './SponsorPage.css';

/**
 * Add a sponsored player (/sponsored-player).
 * The backend has no provider-creates-player endpoint yet, so this form validates and then says so
 * honestly — it never pretends an account was created and nothing leaves the browser.
 */
const EMPTY = { first: '', last: '', email: '', dob: '', height: '', side: 'right', cond: 'stroke', notes: '' };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CONDITIONS = [
    { value: 'stroke', label: 'Post-stroke' },
    { value: 'ortho', label: 'Orthopaedic / post-surgery' },
    { value: 'neuro', label: 'Neurological (MS, Parkinson’s)' },
    { value: 'tbi', label: 'Traumatic brain injury' },
    { value: 'cp', label: 'Cerebral palsy' },
    { value: 'other', label: 'Other' },
];
const SIDES = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'both', label: 'Both' },
    { value: 'none', label: 'None' },
];
const ORDER = ['first', 'last', 'email', 'dob', 'height'];

function validate(v) {
    const h = parseFloat(v.height);
    const today = new Date().toISOString().slice(0, 10);
    return {
        first: !v.first.trim() && 'Enter a first name.',
        last: !v.last.trim() && 'Enter a last name.',
        email: !EMAIL_RE.test(v.email.trim()) && (v.email.trim() ? 'Enter a valid email, like name@example.com.' : 'Enter an email so the player can sign in.'),
        dob: (!v.dob && 'Enter a date of birth.') || (v.dob > today && 'Date of birth can’t be in the future.'),
        height: v.height !== '' && (Number.isNaN(h) || h < 0.5 || h > 2.5) && 'Height must be between 0.5 and 2.5 m.',
    };
}

function SponsorPage() {
    const [v, setV] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [notice, setNotice] = useState(false);
    const noticeRef = useRef(null);

    const errs = validate(v);
    const bad = ORDER.filter((k) => errs[k]);
    const err = (k) => (tried ? errs[k] || undefined : undefined);
    const set = (k) => (e) => { setV((s) => ({ ...s, [k]: e.target.value })); setNotice(false); };
    const setVal = (k) => (val) => { setV((s) => ({ ...s, [k]: val })); setNotice(false); };

    const submit = (e) => {
        e.preventDefault();
        setTried(true);
        if (bad.length) {
            setNotice(false);
            requestAnimationFrame(() => document.getElementById(`ap-${bad[0]}`)?.focus());
            return;
        }
        setNotice(true);
        requestAnimationFrame(() => noticeRef.current?.focus());
    };

    return (
        <div className="eg-page ap-page">
            <div className="ap-head">
                <Breadcrumb items={[{ label: 'Players', to: '/players' }, { label: 'Add player' }]} />
                <h1 className="eg-h1">Add a sponsored player</h1>
            </div>

            {tried && bad.length ? (
                <div className="ap-banner">
                    <Banner tone="danger" title={bad.length === 1 ? '1 field needs attention.' : `${bad.length} fields need attention.`} />
                </div>
            ) : null}
            {notice ? (
                <div ref={noticeRef} tabIndex={-1} className="ap-banner">
                    <Banner tone="warning" title="Not available yet." role="status">
                        No account was created.
                    </Banner>
                </div>
            ) : null}

            <form className="ap-form" onSubmit={submit} noValidate>
                <div className="ap-groups">
                    <fieldset className="ap-group">
                        <legend className="ap-legend">Player</legend>
                        <div className="ap-fields">
                            <TextField id="ap-first" label="First name" autoComplete="off" value={v.first} onChange={set('first')} error={err('first')} />
                            <TextField id="ap-last" label="Last name" autoComplete="off" value={v.last} onChange={set('last')} error={err('last')} />
                            <TextField
                                id="ap-email"
                                className="ap-span"
                                label="Email"
                                type="email"
                                autoComplete="off"
                                value={v.email}
                                onChange={set('email')}
                                error={err('email')}
                            />
                            <TextField id="ap-dob" label="Date of birth" type="date" value={v.dob} onChange={set('dob')} error={err('dob')} />
                            <TextField
                                id="ap-height"
                                label={<>Height <span className="ap-opt">Optional · metres</span></>}
                                type="number"
                                inputMode="decimal"
                                min="0.5"
                                max="2.5"
                                step="0.01"
                                placeholder="1.65"
                                value={v.height}
                                onChange={set('height')}
                                error={err('height')}
                            />
                        </div>
                    </fieldset>

                    <fieldset className="ap-group">
                        <legend className="ap-legend">Clinical</legend>
                        <div className="ap-fields">
                            <div className="eg-field">
                                <span className="eg-field__label">Affected side</span>
                                <Segmented label="Affected side" options={SIDES} value={v.side} onChange={setVal('side')} />
                            </div>
                            <SelectField id="ap-cond" label="Primary condition" value={v.cond} onChange={setVal('cond')} options={CONDITIONS} />
                            <div className="eg-field ap-span">
                                <label htmlFor="ap-notes" className="eg-field__label">Notes for the care team <span className="ap-opt">Optional</span></label>
                                <textarea
                                    id="ap-notes"
                                    className="ap-textarea"
                                    rows={3}
                                    placeholder="Precautions, goals"
                                    value={v.notes}
                                    onChange={set('notes')}
                                />
                            </div>
                        </div>
                    </fieldset>
                </div>

                <aside className="ap-aside" aria-label="Sponsorship">
                    <span className="ap-aside__title">Sponsorship</span>
                    <div className="ap-aside__note">
                        <Icon name="clock" size={20} />
                        <span>Not connected yet · nothing is saved</span>
                    </div>
                    <Button type="submit" variant="primary" size="lg" block>Add player</Button>
                    <Button variant="ghost" muted block to="/players">Cancel</Button>
                </aside>
            </form>
        </div>
    );
}

export default SponsorPage;
